import React from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import dayjs from 'dayjs';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer/CommonEMAirlineContainer';
//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonBreakingDealsCarousel';
import Loading from '../../others/Loading';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';
import SecondaryFilters from '../BreakingDealsContainer/atoms/SecondaryFilters';
import HeaderContainer from '../../elements/HeaderContainer';
import BookingPopup from '../BreakingDealsContainer/atoms/BookingPopup';
import Carousel from '../../controls/ElasticCarousel';
import FareItemCard from './atoms/FareItemCard/FareItemCard';
import SeeMore from './atoms/SeeMore/SeeMore';

//-------------------------------------
// Every mundo integration
//-------------------------------------
import { getFares } from '../../../_integrations/everymundo/breakingDeals';
import { getAirports } from '../../../_integrations/everymundo/breakingDealsAirports';
import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/groupedRoutesAggregation';
import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useFareItemProps } from '../_commons/tracking';
import { isDayAfter } from '../../../helpers/date';
import { transformNumbersDays } from '../../../helpers/utils';
import { formatDate } from '../../../_integrations/everymundo/tracking/helpers';
import { publish } from '../../../_integrations/everymundo/tracking/pubsub';

function BreakingDealsCarouselContainer(props) {
  const {
    prePopulation,
    integrationSettings,
    paginationSettings,
    meta,
    getAirports: getAirportsFn,
    getFares: getFaresFn,
    getTravelClasses: getTravelClassesFn,
    direction,
    faresTrasformationLogic,
    showFilters,
    initialDestinationCards,
    initialDepartureAirports,
    filters: { availableFilters, ...filters },
    showSkeletonScreen,
    loading,
    showLoading,
    noResultsPlaceHolder,
    alert,
    showDisclaimer,
    mainContentSettings,
    disclaimerContentSettings,
    disclaimer: disclaimerSettings,
    skeletonSettings,
    noFaresBehavior,
    theme,
    // deprecated props
    filterContentSettings, // use filters.containerSettings
    locationSelector, // use filters.originDestinationSettings
    budgetSetting, // use filters.budgetSettings
    clearAll, // use filters.clearAllSettings
    originDestinationLogic,
    secondaryFilters,
    showSecondaryFilters,
    enableSEO = true,
    bookingPopupSettings,
    isIframe,
    carouselSettings,
    showSeeMoreDeals,
    seeMoreContentSettings,
    seeMoreSettings,
    pageContext
  } = props;

  const [className, style] = useStyles(props, { direction, theme });
  const myStyle = Object.assign({ minHeight: '600px' }, style);

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    containerSettings: filterContentSettings,
    originDestinationSettings: {
      components: {
        origin: locationSelector,
        destination: locationSelector
      }
    },
    budgetSettings: budgetSetting,
    clearAllSettings: clearAll,
    ...filters
  };

  // const { focusIndex, setFocusIndex, ...focusHandlers } = useListFocusHandlers();
  const secondaryFiltersRef = React.useRef(null);

  const {
    fares: { match, ...fares },
    tracking,
    dispatch,
    retry,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: {
      prePopulation,
      integrationSettings,
      meta,
      getAirports: getAirportsFn,
      getTravelClasses: getTravelClassesFn
    },
    initialData: initialDestinationCards,
    minInitialResults: noFaresBehavior === 'hidden' ? 1 : null,
    initialOrigins: initialDepartureAirports,
    getFares: getFaresFn,
    paginationSettings,
    faresTransformationLogic: faresTrasformationLogic,
    availableFilters: showFilters ? availableFilters : [],
    showSecondaryFilters,
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : [],
    hideBudgetOnMultiCurrency: filters.hideBudgetOnMultiCurrency,
    getTravelClassesApi: secondaryFilters.getTravelClassesApi,
    showOrigin:
      filtersConfig.originDestinationSettings && filtersConfig.originDestinationSettings.showOrigin,
    showDestination:
      filtersConfig.originDestinationSettings &&
      filtersConfig.originDestinationSettings.showDestination,
    datalayerTracking: true,
    pageContext
  });

  const fareItemOnClickAction = (fareData) => {
    let newDepartureDate = dayjs().toDate();
    if (
      fareData.departureDate &&
      isDayAfter(dayjs(fareData.departureDate).toDate(), dayjs().toDate())
    ) {
      newDepartureDate = dayjs(fareData.departureDate).toDate();
    }

    const popupData = {
      name: fareData.name,
      destinationImage: fareData.destinationImage,
      destination: fareData.destination,
      destinationCity: fareData.destinationCity,
      origin: fareData.origin,
      originCity: fareData.originCity,
      departureStart: fareData.departureDate,
      departureEnd: fareData.returnDate,
      departureDate: formatDate(newDepartureDate),
      returnDate: fareData.minStay
        ? formatDate(
            dayjs(newDepartureDate.toString())
              .add(transformNumbersDays(fareData.minStay), 'd')
              .toDate()
          )
        : formatDate(newDepartureDate),
      minStay: fareData.minStay,
      maxStay: fareData.maxStay,
      formattedTotalPrice: fareData.formattedTotalPrice,
      journeyType: fareData.journeyType,
      farenetTravelClass: fareData.farenetTravelClass,
      currencyCode: fareData.currencyCode,
      terms: fareData.fareTerms,
      bookUntilDate: fareData.bookUntilDate,
      airlineCode: fareData.airline,
      siteEdition: fareData.market,
      passengers: {
        options: [
          {
            name: 'passenger1',
            label: 'passenger1-key',
            description: 'passenger1-desc-key',
            maxCount: 9,
            minCount: fareData.min_adult,
            enabled: fareData.adults
          },
          {
            name: 'passenger2',
            label: 'passenger2-key',
            description: 'passenger2-desc-key',
            enabled: fareData.children,
            maxCount: 8
          },
          {
            name: 'passenger3',
            label: 'passenger3-key',
            description: 'passenger3-desc-key',
            enabled: fareData.infant,
            maxCount: 9
          },
          {
            name: 'passenger4',
            label: 'passenger4-key',
            description: 'passenger4-desc-key',
            enabled: fareData.infant_with_seat,
            maxCount: 9
          },
          {
            name: 'passenger5',
            label: 'passenger5-key',
            description: 'passenger5-desc-key',
            enabled: fareData.overseas_filipino_worker,
            maxCount: 9
          },
          {
            name: 'passenger6',
            label: 'passenger6-key',
            description: 'passenger6-desc-key',
            enabled: fareData.youth_or_youth_of_uk === 1 ? true : false,
            maxCount: 8
          },
          {
            name: 'passenger7',
            label: 'passenger7-key',
            description: 'passenger7-desc-key',
            enabled: fareData.youth_or_youth_of_uk === 2 ? true : false,
            maxCount: 8
          }
        ],
        rules: [
          `maxInfantPerAdult:${fareData.max_infant_per_adult}`,
          `maxChildrenPerAdult:${fareData.max_children_per_adult}`,
          `maxInfantAndChildrenPerAdult:${fareData.max_infant_and_children_per_adult}`
        ],
        totalPassengers: fareData.max_passengers
      }
    };
    publish('em.fare.popup.open', popupData);
    dispatch({
      type: 'OPEN_BOOKING',
      payload: { fare: popupData, callToActionLabel: 'Fare-Card' }
    });
  };

  const itemSettings = useFareItemProps(
    carouselSettings.itemSettings,
    dispatch,
    fareItemOnClickAction
  );

  let newItemsSettings = {
    ...itemSettings
  };

  if (fares.data && fares.data.length === 1) {
    newItemsSettings = {
      ...newItemsSettings,
      fareItemBackgroundImage: {
        ...newItemsSettings.fareItemBackgroundImage,
        imageResizingParams: {
          worker: 'https://assets.airtrfx.com/cdn-cgi/image/',
          height: 720,
          width: 1280,
          quality: 90,
          fit: 'crop',
          format: 'auto',
          opt: true
        },
        ...newItemsSettings.fareItemBackgroundImageOneItem
      }
    };
  }

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }
  if (isIframe && !isSSR()) {
    document.body.style.overflow = 'hidden';
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }
  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  return (
    <>
      <Box
        position="relative"
        className={`${className} ${BreakingDealsCarouselContainer.componentName}`}
        style={myStyle}
        dataTestId="breakingDealsContainer"
      >
        {showLoading && match('LOADING') && <Loading {...loading} />}
        {showSkeletonScreen && match('SKELETON') && (
          <Skeleton {...skeletonSettings} position="absolute" width="100" />
        )}
        <HeaderContainer
          defaultTitleValue={'Trending destination'}
          handleSeeMoreDeals={handleSeeMoreDeals}
          {...props}
        />

        {showSecondaryFilters && (
          <SecondaryFilters
            ref={secondaryFiltersRef}
            {...filtersConfig}
            {...secondaryFilters}
            {...filtersState}
            availableFilters={filtersState.availableSecondaryFilters}
            filter={{
              ...{ prePopulation, integrationSettings, meta, getAirports }
            }}
            loading={match('SKELETON')}
          />
        )}
        <Row {...mainContentSettings}>
          {match('ERROR') && (
            <Col>
              <ContainerErrorAlert {...alert} tryAgain={retry} />
            </Col>
          )}
          {match('NO_RESULTS') && (
            <Col>
              <NoFeaturedPopularFaresAlert {...noResultsPlaceHolder} />
            </Col>
          )}
          {match('COMPLETE') && (
            <>
              <Col>
                <Carousel
                  {...carouselSettings}
                  itemSettings={newItemsSettings}
                  ItemComponentInstance={FareItemCard}
                  itemsData={fares.data}
                  tracking={tracking}
                />
              </Col>
              <BookingPopup isIframe={isIframe} {...bookingPopupSettings} />
            </>
          )}
        </Row>
        {showSeeMoreDeals && (
          <Row {...seeMoreContentSettings}>
            <SeeMore
              labelKey="cmp-breaking-deals-carousel-container-see-more"
              siteEdition={pageContext.siteEdition}
              {...seeMoreSettings}
            />
          </Row>
        )}
        {showDisclaimer && (
          <Row {...disclaimerContentSettings}>
            <Col>
              <Disclaimer
                labelKey="cmp-breaking-deals-carousel-container-disclaimer"
                {...disclaimerSettings}
              />
            </Col>
          </Row>
        )}
      </Box>
    </>
  );
}

BreakingDealsCarouselContainer.propTypes = {
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to budget
   */
  budgetSetting: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Props of clearAll
   */
  clearAll: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to dates range selector
   */
  datesRangeSelector: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to destination item
   */
  destinationItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Direction of the component
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Function to getFares
   */
  getFares: PropTypes.func,
  /**
   * Used to prepopulate the component
   */
  initialDepartureAirports: PropTypes.arrayOf(PropTypes.any),
  /**
   * Used to prepopulate the component
   */
  initialDestinationCards: PropTypes.arrayOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to location selector
   */
  locationSelector: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: PropTypes.oneOf(['hidden', 'showNoResults']),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other fixed title
   */
  otherPopularDestinationsLabel: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other variable title
   */
  otherPopularDestinationsValue: PropTypes.objectOf(PropTypes.any),
  /**
   * pageContext settings
   */
  pageContext: PropTypes.objectOf(PropTypes.any),
  /**
   * Pagination Component
   */
  pagination: PropTypes.objectOf(PropTypes.any),
  /**
   * Settings of the Pagination
   */
  paginationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showAirportIataCode: PropTypes.bool,
  /**
   * Determine if the component will show the disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Determine if the component will show the filters
   */
  showFilters: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Shows Pagination Component
   */
  showPagination: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  titleKey: PropTypes.string,
  /**
   * Title Component
   */
  titleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to translate
   */
  translate: PropTypes.func,
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * isIframe
   */
  isIframe: PropTypes.bool,

  showSeeMoreDeals: PropTypes.bool
};

BreakingDealsCarouselContainer.defaultProps = {
  fareItemInteractionType: 'deeplink',
  alert: {
    bgColor: 'light',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded',
    labelKeyTitle: 'cmp-container-error-message',
    labelKeySubTitle: 'cmp-container-error-reload-deals'
  },
  budgetSetting: {},
  containerType: 'stateful',
  clearAll: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    borderNone: true,
    icon: {
      iconColor: 'light-18'
    }
  },
  destinationItem: {},
  direction: 'ltr',
  disclaimer: {},
  disclaimerContentSettings: {
    marginTop: '2'
  },
  fareItemOnClickAction: null,
  faresTrasformationLogic: 'grouped-routes',
  filters: {
    availableFilters: ['OriginDestination', 'SingleDateRangePicker', 'Budget']
  },
  filterContentSettings: {
    marginWidth: '-5px'
  },
  getAirports,
  getFares,
  initialDepartureAirports: null,
  initialDestinationCards: null,
  loading: {},
  locationSelector: {},
  listSettings: {},
  mainContentSettings: {
    marginTop: '3'
  },
  noFaresBehavior: 'hidden',
  noResultsPlaceHolder: {
    bgColor: 'light',
    rounded: '@theme:card.rounded'
  },
  otherPopularDestinationsLabel: {},
  otherPopularDestinationsValue: {},
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  pagination: {},
  paginationSettings: {
    type: 'load_more',
    step: 6,
    limit: 30,
    initialVisibleItems: 10
  },
  prePopulation: prePopulationDefaultProps,
  showAirportIataCode: true,
  showDisclaimer: true,
  showFilters: true,
  showSkeletonScreen: true,
  showLoading: true,
  showPagination: true,
  showTitle: true,
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  titleKey: 'cmp-breaking-deals-carousel-container-title',
  titleSettings: {
    margin: '0',
    textAlign: 'left'
  },
  translate: () => {},
  localizationData: null,
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'BreakingDeals'
  },
  theme: null,
  integrationSettings: {},
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-breaking-deals-carousel-container-sub-title',
  subTitleSettings: {},
  isIframe: false,
  showSeeMoreDeals: true
};
BreakingDealsCarouselContainer.componentName = 'BreakingDealsCarouselContainer';

export default withCommonEMContainer(BreakingDealsCarouselContainer);
